export type QuizQuestion = {
  id: string;
  subject: string;
  question: string;
  options: string[];
  correct: number;
  explanation?: string;
  courseId?: string;
  moduleId?: string;
  chapterId?: string;
  topicId?: string;
  topic?: string;
  chapterTitle?: string;
  topicTitle?: string;
  difficulty?: string;
  sourceReference?: string;
  instruction?: string;
  sectionId?: string;
  sectionTitle?: string;
  sourceId?: string;
  sourceTitle?: string;
  sourceContent?: string;
  optionLabels?: string[];
  questionType?: string;
  figure_status?: "none" | "required" | "uploaded";
  figure_image?: string | null;
  figure_alt?: string | null;
  figure_location?: {
    source: string;
    pdfPage: number;
    region?: string;
    label?: string;
    description?: string;
    bbox?: number[];
  } | null;
  passageId?: string | null;
  passage_id?: string | null;
};

export type QuizSession = {
  title: string;
  questions: QuizQuestion[];
  source?: "exam" | "ai" | "practice";
  examId?: string;
  subject?: string;
  subjectSlug?: string;
  answers?: number[];
  currentQuestion?: number;
  updatedAt?: number;
  extractionWarnings?: string[];
  partialExtraction?: boolean;
  courseId?: string;
  moduleId?: string;
  chapterId?: string;
  topicId?: string;
  difficulty?: string;
  timeLimitMinutes?: number;
  duration?: number;
  year?: string;
  passages?: Record<string, { id?: string; title?: string | null; text?: string; body?: string }>;
  sourcesMap?: Record<string, { title: string; content: string; citation?: string }>;
  sectionsMap?: Record<string, { title: string; original_instructions?: string; marks?: string }>;
};

const STORAGE_KEY = "soorgaa_quiz_session";
const PERSISTENT_CACHE_KEY = "soorgaa_practice_history_v1";

export type CachedSession = {
  id: string;
  type: "exam" | "quiz" | "notes";
  title: string;
  subject: string;
  session?: QuizSession;
  answers?: number[];
  currentQuestion?: number;
  notesContent?: string;
  classificationLabel?: string;
  updatedAt: number;
};

export function saveCachedSession(cacheItem: CachedSession): void {
  try {
    const raw = localStorage.getItem(PERSISTENT_CACHE_KEY);
    const map: Record<string, CachedSession> = raw ? JSON.parse(raw) : {};
    map[cacheItem.id] = { ...cacheItem, updatedAt: Date.now() };
    localStorage.setItem(PERSISTENT_CACHE_KEY, JSON.stringify(map));
  } catch (e) {
    console.error("Failed to save session cache", e);
  }
}

export function getCachedSession(id: string): CachedSession | null {
  try {
    const raw = localStorage.getItem(PERSISTENT_CACHE_KEY);
    if (!raw) return null;
    const map: Record<string, CachedSession> = JSON.parse(raw);
    return map[id] || null;
  } catch {
    return null;
  }
}

export function listCachedSessions(): CachedSession[] {
  try {
    const raw = localStorage.getItem(PERSISTENT_CACHE_KEY);
    if (!raw) return [];
    const map: Record<string, CachedSession> = JSON.parse(raw);
    return Object.values(map).sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

export function clearCachedSession(id: string): void {
  try {
    const raw = localStorage.getItem(PERSISTENT_CACHE_KEY);
    if (!raw) return;
    const map: Record<string, CachedSession> = JSON.parse(raw);
    delete map[id];
    localStorage.setItem(PERSISTENT_CACHE_KEY, JSON.stringify(map));
  } catch {}
}

export function saveQuizSession(session: QuizSession): void {
  const updatedSession = { ...session, updatedAt: Date.now() };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSession));
  const cacheId = updatedSession.examId ? `exam:${updatedSession.examId}` : `quiz:${updatedSession.subject || updatedSession.title}`;
  saveCachedSession({
    id: cacheId,
    type: updatedSession.source === "exam" ? "exam" : "quiz",
    title: updatedSession.title,
    subject: updatedSession.subject || updatedSession.questions[0]?.subject || "General",
    session: updatedSession,
    answers: updatedSession.answers,
    currentQuestion: updatedSession.currentQuestion ?? 0,
    updatedAt: Date.now(),
  });
}

export function loadQuizSession(): QuizSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as QuizSession;
    if (!data?.questions?.length) return null;
    return data;
  } catch {
    return null;
  }
}

export function saveQuizAnswers(answers: number[], currentQuestion?: number): void {
  const session = loadQuizSession();
  if (!session) return;
  const updatedSession = { ...session, answers, currentQuestion: currentQuestion ?? session.currentQuestion };
  saveQuizSession(updatedSession);
}

export function clearQuizSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function examContentToQuiz(
  title: string,
  rawContent: {
    metadata?: Record<string, unknown>;
    sections?: Array<{ id: string; title?: string; marks?: string; original_instructions?: string }>;
    sources?: Array<{ id: string; title?: string; content?: string; citation?: string }>;
    questions?: Array<{
      number?: number;
      text?: string;
      section_id?: string;
      section?: string;
      instruction?: string;
      source_id?: string;
      question_type?: string;
      options?: Array<{ label: string; text: string }>;
      answer?: { label?: string | null; text?: string; explanation?: string };
    }>;
  },
  subject = "Exam",
): QuizQuestion[] {
  const sourcesMap: Record<string, { title: string; content: string; citation?: string }> = {};
  if (Array.isArray(rawContent.sources)) {
    rawContent.sources.forEach((s) => {
      if (s.id) {
        sourcesMap[s.id] = {
          title: s.title || "Reference Passage",
          content: s.content || "",
          citation: s.citation,
        };
      }
    });
  }

  const sectionsMap: Record<string, { title: string; original_instructions?: string; marks?: string }> = {};
  if (Array.isArray(rawContent.sections)) {
    rawContent.sections.forEach((sec) => {
      if (sec.id) {
        sectionsMap[sec.id] = {
          title: sec.title || "",
          original_instructions: sec.original_instructions,
          marks: sec.marks,
        };
      }
    });
  }

  const questions = rawContent.questions || [];
  const out: QuizQuestion[] = [];

  questions.forEach((q, idx) => {
    const text = (q.text || "").trim();
    const rawOpts = q.options || [];
    const opts = rawOpts.map((o) => (o.text || "").trim()).filter(Boolean);
    if (!text) return;

    const labels = rawOpts.map((o) => (o.label || "").trim().toUpperCase());
    const answerLabel = (q.answer?.label || "").trim().toUpperCase();

    let correct = 0;
    if (answerLabel && rawOpts.length > 0) {
      const found = rawOpts.findIndex(
        (o) => (o.label || "").trim().toUpperCase() === answerLabel,
      );
      if (found >= 0) correct = found;
    }

    const explanation = (q.answer?.explanation || "").trim();

    // Source passage mapping
    let sourceTitle: string | undefined;
    let sourceContent: string | undefined;

    if (q.source_id && sourcesMap[q.source_id]) {
      sourceTitle = sourcesMap[q.source_id].title;
      sourceContent = sourcesMap[q.source_id].content;
    }

    // Section mapping
    let sectionTitle: string | undefined = q.section;
    let instruction: string | undefined = q.instruction;

    if (q.section_id && sectionsMap[q.section_id]) {
      const sec = sectionsMap[q.section_id];
      if (!sectionTitle) sectionTitle = sec.title;
      if (!instruction) instruction = sec.original_instructions;
    }

    const rawType = (q.question_type || "multiple_choice").toLowerCase().trim();
    const questionType: "multiple_choice" | "true_false" =
      rawType.includes("true") || rawType.includes("tf") ? "true_false" : "multiple_choice";

    const qPassageId = (q as any).passageId || (q as any).passage_id || null;

    out.push({
      id: String(q.number ?? idx + 1),
      subject,
      question: text,
      options: opts.length > 0 ? opts : [q.answer?.text || "Answer provided in explanation"],
      correct: opts.length > 0 ? Math.min(correct, opts.length - 1) : 0,
      optionLabels: labels.length > 0 ? labels : undefined,
      questionType,
      instruction,
      sectionId: q.section_id,
      sectionTitle,
      sourceId: q.source_id,
      sourceTitle,
      sourceContent,
      passageId: qPassageId,
      passage_id: qPassageId,
      figure_status: (q as { figure_status?: "none" | "required" | "uploaded" }).figure_status,
      figure_image: (q as { figure_image?: string | null }).figure_image,
      figure_alt: (q as { figure_alt?: string | null }).figure_alt,
      figure_location: (q as { figure_location?: any }).figure_location,
      ...(explanation ? { explanation } : {}),
    });
  });

  return out;
}
